import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import OrderDesign from './OrderDesign';
import { useTranslation } from 'react-i18next';
import { playSoundEffect } from './hooks/useSoundEffects';
import CustomDropdown from './CustomDropdown';
import './StrategyModal.css';

// Helper function to get rarity sort order
const getRarityOrder = (rarity) => {
  const rarityOrder = { 'common': 1, 'rare': 2, 'epic': 3, 'legendary': 4 };
  return rarityOrder[(rarity || 'common').toLowerCase()] || 0;
};

// Faction colors for strategy display
const factionColors = {
  'Remnants of Humanity': '#556B2F',
  'Exolot Dominion': '#4B0082',
  'League of Shadows': '#1A1A1A',
  'Ra\'s Children': '#F4A460',
  'Asrah': '#8B0000',
  'The Sentients': '#ADD8E6',
  'Combined': '#FFA500'
};

const MAX_HAND_SIZE = 10;

/** True when the player has enough intel to play this order (matches hand afford checks in BattleUI). */
function canAffordOrderWithCurrentIntel(order, playerState) {
  if (!playerState || !order) return false;
  const intelNum = Number(playerState.intelligence);
  const costNum = Number(order.cost ?? 0);
  if (!Number.isFinite(intelNum) || !Number.isFinite(costNum)) return false;
  return intelNum >= costNum;
}

const combinedFactions = {
  'The Conspiracy': ['The Conspiracy', 'League of Shadows', 'Asrah', 'The Sentients'],
  'The Corrupted Core': ['The Corrupted Core', 'Exolot Dominion', 'Asrah', 'The Sentients'],
  'The Covenant': ['The Covenant', 'Ra\'s Children', 'League of Shadows', 'The Sentients'],
  'The Divine Syndicate': ['The Divine Syndicate', 'Ra\'s Children', 'Exolot Dominion', 'The Sentients'],
  'The Forsaken Pact': ['The Forsaken Pact', 'Remnants of Humanity', 'Asrah', 'The Sentients'],
  'The Hollow': ['The Hollow', 'Ra\'s Children', 'Asrah', 'The Sentients'],
  'The Iron Alliance': ['The Iron Alliance', 'Remnants of Humanity', 'Exolot Dominion', 'The Sentients'],
  'The Last Crusade': ['The Last Crusade', 'Ra\'s Children', 'Remnants of Humanity', 'The Sentients'],
  'The Shadow Consortium': ['The Shadow Consortium', 'League of Shadows', 'Exolot Dominion', 'The Sentients'],
  'The Silent Accord': ['The Silent Accord', 'Remnants of Humanity', 'League of Shadows', 'The Sentients']
};

function StrategyModal({
  show,
  onClose,
  strategy,
  gameState,
  userId = null,
  hoveredOrderIndex,
  onHoverOrder,
  playerState,
  commandDirectiveActive = false,
  onCommandDirectiveSelect = null,
  isMobile = false,
}) {
  const { t } = useTranslation();
  const [hoveredOrderId, setHoveredOrderId] = useState(null);
  const [sortBy, setSortBy] = useState('cost'); // 'cost', 'rarity', 'name', 'type'

  if (!show || !strategy?.orders) return null;

  const handSize = Array.isArray(playerState?.hand) ? playerState.hand.length : 0;
  const isHandFull = handSize >= MAX_HAND_SIZE;

  // Expand orders based on count and track which ones are drawn
  const expandedOrders = [];
  const drawnCounts = new Map(); // Track how many of each order ID have been drawn
  
  // First, count how many of each order have been drawn
  if (playerState) {
    const allDrawnOrders = [
      ...(playerState.hand || []),
      ...(playerState.discard || []),
      ...(playerState.playedOrders?.front || []),
      ...(playerState.playedOrders?.flank || []),
      ...(playerState.completedOrders || [])
    ];
    
    allDrawnOrders.forEach(order => {
      if (order?.id) {
        drawnCounts.set(order.id, (drawnCounts.get(order.id) || 0) + 1);
      }
    });
  }

  strategy.orders.forEach(order => {
    const count = order.count || 1;
    const drawnCount = drawnCounts.get(order.id) || 0;
    
    for (let i = 0; i < count; i++) {
      expandedOrders.push({ 
        ...order,
        isDrawn: i < drawnCount // Mark first N copies as drawn
      });
    }
  });
  
  // Sort by selected option
  expandedOrders.sort((a, b) => {
    // Primary sort by selected option
    let primaryComparison = 0;
    switch (sortBy) {
      case 'cost':
        primaryComparison = (a.cost ?? 0) - (b.cost ?? 0);
        break;
      case 'rarity':
        primaryComparison = getRarityOrder(a.rarity) - getRarityOrder(b.rarity);
        break;
      case 'name':
        primaryComparison = (a.name || '').localeCompare(b.name || '');
        break;
      case 'type':
        primaryComparison = (a.type || '').localeCompare(b.type || '');
        break;
      default:
        primaryComparison = (a.cost ?? 0) - (b.cost ?? 0);
    }

    if (primaryComparison !== 0) return primaryComparison;

    // Secondary sort by name (except when already sorting by name)
    if (sortBy !== 'name') {
      return (a.name || '').localeCompare(b.name || '');
    }

    // If sorting by name and names are equal, sort by cost
    return (a.cost ?? 0) - (b.cost ?? 0);
  });
  
  const hoveredOrder = hoveredOrderIndex !== null ? expandedOrders[hoveredOrderIndex] : null;

  const modalTree = (
    <div
      className={`strategy-modal-overlay${isMobile ? ' strategy-modal-overlay--mobile' : ''}`}
      data-no-battle-flashlight="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      {/* Desktop: hover preview beside modal — off-screen on mobile */}
      {hoveredOrder && !isMobile && (
        <div
          style={{
            position: 'absolute',
            left: 'calc(50% + 300px)',
            top: '50%',
            transform: 'translateY(-50%)',
            marginLeft: '20px',
            zIndex: 15101,
            pointerEvents: 'none',
          }}
        >
          <OrderDesign
            order={hoveredOrder}
            count={0}
            minimal={false}
            lazyLoad={true}
            gameState={gameState}
            userId={userId}
          />
        </div>
      )}
      <div
        className={`strategy-modal-panel${isMobile ? ' strategy-modal-panel--mobile' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="strategy-modal-header">
          <h3 style={{ color: 'white', margin: 0 }}>
            {t('battleUI.strategy') || 'Strategy'}
          </h3>
          <button
            type="button"
            className="strategy-modal-close-btn"
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              onClose();
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#444';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = '#333';
            }}
          >
            {t('battleUI.close') || 'Close'}
          </button>
        </div>

        <div className="strategy-modal-sort-row">
          <div style={{ color: '#aaa', fontSize: isMobile ? '0.8em' : '0.9em' }}>
            {t('battleUI.totalStrategyOrders') || 'Total'}: {expandedOrders.length}
          </div>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              color: '#aaa',
              fontSize: isMobile ? '0.8em' : '0.9em',
              whiteSpace: isMobile ? 'normal' : 'nowrap',
            }}
          >
            {t('strategy.sortBy') || 'Sort by:'}
            <div
              className="strategy-modal-sort-dropdown"
              style={{ minWidth: isMobile ? undefined : '150px', maxWidth: isMobile ? '100%' : '150px' }}
            >
              <CustomDropdown
                options={[
                  { value: 'cost', label: t('strategy.sort.cost') || 'Cost' },
                  { value: 'rarity', label: t('strategy.sort.rarity') || 'Rarity' },
                  { value: 'name', label: t('strategy.sort.name') || 'Name' },
                  { value: 'type', label: t('strategy.sort.type') || 'Type' },
                ]}
                value={sortBy}
                onChange={(value) => {
                  playSoundEffect('/sounds/click.mp3');
                  setSortBy(value);
                }}
                placeholder={t('strategy.sortBy') || 'Sort by:'}
              />
            </div>
          </label>
        </div>

        <div className="strategy-modal-list">
          {expandedOrders.map((order, index) => {
            const color = order
              ? (combinedFactions[order.faction_name]
                  ? factionColors['Combined']
                  : factionColors[order.faction_name] || '#FFFFFF')
              : '#FFFFFF';

            const isDrawn = order.isDrawn || false;
            const isHovered = hoveredOrderId === order.id;
            const canAffordGet = canAffordOrderWithCurrentIntel(order, playerState);

            return (
              <div
                key={`strategy_${order.id || index}_${index}`}
                onMouseEnter={() => {
                  if (!isMobile) {
                    playSoundEffect('/sounds/hoverover.mp3', 0.3);
                  }
                  onHoverOrder(index);
                  setHoveredOrderId(order.id);
                }}
                onMouseLeave={() => {
                  onHoverOrder(null);
                  setHoveredOrderId(null);
                }}
                className="strategy-modal-order-row"
                style={{
                  backgroundColor: color,
                  opacity: isDrawn ? 0.4 : 1,
                  filter: isDrawn ? 'grayscale(0.6)' : 'none',
                }}
              >
                <div>
                  <strong>{order.name || 'Unknown Order'}</strong>
                </div>
                <div className="strategy-modal-order-meta">
                  <span>{order.cost ?? 'N/A'}</span>
                  {commandDirectiveActive &&
                    !isDrawn &&
                    onCommandDirectiveSelect &&
                    (isMobile || isHovered) && (
                    <button
                      type="button"
                      className={`strategy-modal-get-btn${canAffordGet && !isHandFull ? ' strategy-modal-get-btn--affordable' : ''}`}
                      disabled={isHandFull}
                      title={isHandFull ? (t('battleUI.handFullCannotGet') || 'Hand is full (10/10)') : undefined}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isHandFull) return;
                        onCommandDirectiveSelect(order.id);
                      }}
                    >
                      {t('battleUI.get') || 'Get'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined' || !document.body) {
    return modalTree;
  }
  return createPortal(modalTree, document.body);
}

export default StrategyModal;

